import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Award, Users, Sprout, TreePine, Scissors, Wrench, Droplets, Shovel, ArrowUpFromLine, CloudRain, Phone, Mail } from "lucide-react";
import { services, heroImage, aboutImage, companyInfo, galleryImages } from "../mock";

const iconMap = { Sprout, TreePine, Scissors, Wrench, Droplets, Shovel, ArrowUpFromLine, CloudRain };

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden bg-sage-800">
        <img
          src={heroImage}
          alt="Giardino Rota Adriano Battista"
          loading="eager"
          fetchPriority="high"
          referrerPolicy="no-referrer"
          className="absolute inset-0 w-full h-full object-cover animate-slow-zoom pointer-events-none"
          onError={(e) => {
            e.currentTarget.src = "https://customer-assets-7cd3h4nn.emergentagent.net/job_modern-garden-4/artifacts/vnkkf43w_FD0DCA1C-9F66-4F3A-AC58-FAD78E2D1D7C.webp";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sage-900/50 via-sage-900/30 to-sage-900/70 pointer-events-none" />

        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto w-full px-6 lg:px-10">
            <div className="max-w-3xl animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-8">
                <Leaf className="w-4 h-4 text-white" strokeWidth={1.5} />
                <span className="text-xs tracking-[0.2em] uppercase text-white">Locatello, Bergamo</span>
              </div>
              <h1 className="font-serif text-white text-5xl md:text-6xl lg:text-7xl leading-[1.05] mb-8">
                Professionalità e <span className="italic text-sage-100">passione</span><br />
                al servizio delle tue aree verdi.
              </h1>
              <p className="text-lg md:text-xl text-white/90 leading-relaxed max-w-2xl mb-10 font-light">
                Progettiamo, creiamo e curiamo spazi verdi unici. Con passione, competenza e attenzione per ogni dettaglio della natura.
              </p>
              <div className="flex flex-wrap gap-4 relative z-20">
                <Link
                  to="/servizi"
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-sage-800 rounded-full text-sm tracking-wide hover:bg-sage-50 transition-all duration-300 hover:shadow-lg cursor-pointer"
                >
                  Scopri i servizi
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contatti"
                  className="inline-flex items-center gap-3 px-8 py-4 border border-white/40 text-white rounded-full text-sm tracking-wide hover:bg-white/10 transition-all duration-300 backdrop-blur-sm cursor-pointer"
                >
                  Richiedi preventivo
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom stats */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-sage-900/80 to-transparent pb-8 pt-16 pointer-events-none">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-2 gap-8 text-white">
              <div>
                <div className="font-serif text-4xl md:text-5xl">{companyInfo.yearsExperience}+</div>
                <div className="text-xs tracking-[0.2em] uppercase text-white/70 mt-1">Anni d'esperienza</div>
              </div>
              <div>
                <div className="font-serif text-4xl md:text-5xl">8</div>
                <div className="text-xs tracking-[0.2em] uppercase text-white/70 mt-1">Servizi specializzati</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 reveal">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <span className="text-xs tracking-[0.3em] uppercase text-sage-600">La nostra mission</span>
              <h2 className="font-serif text-4xl md:text-5xl text-sage-900 mt-4 leading-tight">
                Un nuovo habitat<br />verde, su misura.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-6 text-lg leading-relaxed text-sage-800/90">
              <p>
                Il giardino dei desideri nasce dall'esigenza di creare la cultura di un nuovo habitat verde. I gusti son gusti e ognuno di noi ha dei propri canoni di bellezza.
              </p>
              <p>
                Ognuno ama fare cose diverse e utilizza il verde in modo diverso: proprio come una stanza della casa, deve essere <em>progettato su misura</em> a seconda delle esigenze personali di chi ne fruisce.
              </p>
              <p className="text-sage-700 italic font-serif text-2xl">
                Sofisticato, elegante, pratico, a misura di bimbo… indistruttibile, o su misura per il tuo animale domestico.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16 reveal">
            <span className="text-xs tracking-[0.3em] uppercase text-sage-600">Cosa facciamo</span>
            <h2 className="font-serif text-4xl md:text-5xl text-sage-900 mt-4 mb-6">I nostri servizi</h2>
            <p className="text-lg text-sage-800/70 leading-relaxed">
              Dalla progettazione alla manutenzione, dalla potatura d'alto fusto al noleggio gru: una soluzione completa per ogni esigenza del tuo spazio verde.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, idx) => {
              const Icon = iconMap[s.icon];
              return (
                <Link
                  key={s.id}
                  to={`/servizi/${s.id}`}
                  className="group relative overflow-hidden rounded-lg bg-sage-50 hover:shadow-xl transition-all duration-500 reveal"
                  style={{ transitionDelay: `${idx * 60}ms` }}
                >
                  <div className="relative h-64 overflow-hidden bg-sage-100">
                    {s.rotate ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img
                          src={s.image}
                          alt={s.title}
                          className="max-w-none transition-transform duration-700 group-hover:scale-110"
                          style={{ height: "180px", width: "auto", transform: "rotate(90deg)" }}
                        />
                      </div>
                    ) : (
                      <img
                        src={s.image}
                        alt={s.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-sage-900/70 via-sage-900/20 to-transparent" />
                    <div className="absolute top-5 left-5 w-12 h-12 rounded-full bg-white/95 backdrop-blur flex items-center justify-center">
                      {Icon && <Icon className="w-5 h-5 text-sage-700" strokeWidth={1.5} />}
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-xl text-sage-900 mb-3">{s.title}</h3>
                    <p className="text-sm text-sage-700/80 leading-relaxed mb-5">{s.description}</p>
                    <span className="inline-flex items-center gap-2 text-sm text-sage-700 group-hover:gap-3 transition-all">
                      Scopri di più <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-24 md:py-32 bg-sage-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="relative reveal">
              <div className="absolute -top-6 -left-6 w-32 h-32 border-2 border-sage-500 rounded" />
              <img
                src={aboutImage}
                alt="Giardiniere professionista"
                className="relative w-full max-h-[440px] object-contain rounded shadow-xl bg-sage-50"
              />
              <div className="absolute -bottom-8 -right-6 bg-white p-6 rounded shadow-xl max-w-[220px] hidden md:block">
                <Award className="w-8 h-8 text-sage-700 mb-3" strokeWidth={1.5} />
                <div className="font-serif text-2xl text-sage-900">Passione</div>
                <div className="text-xs text-sage-600 mt-1">Sin da piccoli, il nostro amore per il verde.</div>
              </div>
            </div>

            <div className="reveal">
              <span className="text-xs tracking-[0.3em] uppercase text-sage-600">Chi siamo</span>
              <h2 className="font-serif text-4xl md:text-5xl text-sage-900 mt-4 mb-8 leading-tight">
                Il lavoro,<br />la nostra passione.
              </h2>
              <p className="text-lg text-sage-800/80 leading-relaxed mb-6">
                {companyInfo.fullName} è un'azienda familiare di Locatello (Bergamo). Da oltre venticinque anni ci prendiamo cura dei giardini delle famiglie e delle aziende della nostra valle.
              </p>
              <p className="text-sage-800/80 leading-relaxed mb-10">
                Ogni intervento nasce dall'ascolto: capire cosa desideri, immaginare insieme, realizzare con precisione artigianale. Perché un giardino non è un lavoro, ma una storia che coltiviamo con te.
              </p>

              <div className="grid grid-cols-2 gap-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center shrink-0">
                    <Users className="w-5 h-5 text-sage-700" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-serif text-lg text-sage-900">Team esperto</div>
                    <div className="text-sm text-sage-700/70">Professionisti certificati</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center shrink-0">
                    <Leaf className="w-5 h-5 text-sage-700" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-serif text-lg text-sage-900">Cura del dettaglio</div>
                    <div className="text-sm text-sage-700/70">Ogni pianta è unica</div>
                  </div>
                </div>
              </div>

              <Link
                to="/chi-siamo"
                className="inline-flex items-center gap-3 px-8 py-4 bg-sage-700 text-white rounded-full text-sm tracking-wide hover:bg-sage-800 transition-all group"
              >
                Scopri la nostra storia
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6 reveal">
            <div>
              <span className="text-xs tracking-[0.3em] uppercase text-sage-600">Le nostre creazioni</span>
              <h2 className="font-serif text-4xl md:text-5xl text-sage-900 mt-4">Fotogallery</h2>
            </div>
            <Link to="/fotogallery" className="inline-flex items-center gap-2 text-sage-700 link-underline text-sm">
              Vedi tutto <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.slice(0, 8).map((g) => (
              <div key={g.id} className="group relative aspect-square overflow-hidden rounded reveal">
                <img src={g.url} alt={g.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-sage-900/0 group-hover:bg-sage-900/40 transition-colors duration-500 flex items-end p-4">
                  <span className="text-white text-sm font-light opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0 duration-500">
                    {g.title}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-sage-800">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${heroImage})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center reveal">
          <span className="text-xs tracking-[0.3em] uppercase text-sage-200">Iniziamo insieme</span>
          <h2 className="font-serif text-4xl md:text-6xl text-white mt-4 mb-6 leading-tight">
            Il tuo giardino, la nostra passione.
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10">
            Preventivo e progettazione grafica sempre gratuiti. Contattaci per un sopralluogo senza impegno.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-3 px-8 py-4 bg-white text-sage-800 rounded-full text-sm tracking-wide hover:bg-sage-50 transition-all">
              <Phone className="w-4 h-4" strokeWidth={1.8} /> {companyInfo.phone}
            </a>
            <Link to="/contatti" className="inline-flex items-center gap-3 px-8 py-4 border border-white/40 text-white rounded-full text-sm tracking-wide hover:bg-white/10 transition-all backdrop-blur-sm">
              <Mail className="w-4 h-4" strokeWidth={1.8} /> Scrivici
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
